import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ApiService } from '../../services/api.service';
import { ApiResponse } from '../../shared/types';
import {
  extractListRows,
  readResponsePagination,
  resolveListPagination,
} from '../../shared/utils/list-pagination.util';

/**
 * A car wash company on the platform.
 *
 * This is the single shape for `admin/super-admin/companies` rows. Impersonation
 * calls it a "shop" in the UI, but it is the same record from the same endpoint —
 * the admin_* fields below are what POST …/impersonate/admin needs.
 */
export interface Company {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  contact?: string;
  address?: string;
  is_active?: boolean;
  logo?: string;
  no_of_locations?: number;
  created_at?: string;
  updated_at?: string;
  /** Company IANA timezone when provided by the companies API */
  timezone?: string;
  /** Company admin user id required by POST …/impersonate/admin */
  admin_user_id?: string;
  admin_name?: string;
  admin_email?: string;
}

export interface CompanyListParams {
  page?: number;
  limit?: number;
  /** Matches name, email, contact, address */
  search?: string;
  status?: boolean | string;
  sort_by?: string;
  sort_order?: string;
}

export interface CompanyListResult {
  companies: Company[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
}

/**
 * Company data for the admin / super-admin context.
 *
 * All calls go through ApiService's super-admin token methods. This is the only
 * client for `admin/super-admin/companies` — the Companies list, the impersonation
 * pages and the company pickers all read through it, so response normalization
 * lives here rather than being repeated per feature.
 */
@Injectable({
  providedIn: 'root',
})
export class CompanyService {
  constructor(private apiService: ApiService) {}

  /**
   * Raw company list. Prefer `listCompaniesPaginated` — it normalizes rows and
   * pagination. This stays for callers that only need the untouched payload.
   */
  getCompanies(params?: CompanyListParams): Observable<ApiResponse<Company[]>> {
    const queryParams: Record<string, string> = {};
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams[key] = value.toString();
        }
      });
    }

    return this.apiService
      .getWithSuperAdminToken<Company[]>(`admin/super-admin/companies`, queryParams)
      .pipe(
        catchError(error => {
          console.error('Error fetching companies:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Company list with rows and pagination normalized to a single shape.
   * Tolerates array roots and `{ companies | shops | data: [...] }` envelopes.
   */
  listCompaniesPaginated(
    params: CompanyListParams = {}
  ): Observable<ApiResponse<CompanyListResult>> {
    return this.getCompanies(params).pipe(
      map(res => ({
        ...res,
        data: this.normalizeCompanyList(res.data, params, readResponsePagination(res)),
      }))
    );
  }

  getCompany(companyId: string): Observable<ApiResponse<Company>> {
    return this.apiService
      .getWithSuperAdminToken<any>(`admin/super-admin/companies/${companyId}`)
      .pipe(
        map(res => {
          const rows = this.normalizeCompanies(res.data);
          return {
            ...res,
            data: rows[0] || { id: companyId, name: 'Company' },
          };
        }),
        catchError(error => {
          console.error('Error fetching company:', error);
          return throwError(() => error);
        })
      );
  }

  private normalizeCompanyList(
    data: any,
    params: CompanyListParams,
    paginationFromResponse: Record<string, any> | null = null
  ): CompanyListResult {
    const page = params.page ?? 1;
    const limit = params.limit ?? 20;

    if (!data) {
      return {
        companies: [],
        totalItems: 0,
        currentPage: page,
        totalPages: 0,
        itemsPerPage: limit,
      };
    }

    const { rows, pagination: nestedPagination } = extractListRows(data, [
      'companies',
      'shops',
      'data',
    ]);
    const companies =
      rows.length > 0
        ? rows.map(row => this.toCompany(row as Record<string, any>))
        : this.normalizeCompanies(data);

    return {
      companies,
      ...resolveListPagination(
        nestedPagination || paginationFromResponse,
        { page, limit },
        companies.length
      ),
    };
  }

  private normalizeCompanies(data: any): Company[] {
    if (!data) {
      return [];
    }
    if (Array.isArray(data)) {
      return data.map(row => this.toCompany(row as Record<string, any>));
    }
    if (typeof data === 'object') {
      const obj = data as Record<string, any>;
      const nested = obj['companies'] ?? obj['data'] ?? obj['company'] ?? obj['shops'];
      if (Array.isArray(nested)) {
        return nested.map(row => this.toCompany(row as Record<string, any>));
      }
      if (obj['id'] || obj['name']) {
        return [this.toCompany(obj)];
      }
    }
    return [];
  }

  private toCompany(row: Record<string, any>): Company {
    const owner = this.asRecord(row['owner']);
    const admin = this.asRecord(row['admin']);

    const adminUserId =
      row['admin_user_id'] ??
      row['admin_id'] ??
      row['owner_id'] ??
      row['user_id'] ??
      owner?.['id'] ??
      admin?.['id'];

    return {
      id: String(row['id'] ?? ''),
      name: String(row['name'] ?? 'Unnamed company'),
      email: this.str(row['email']),
      phone: this.str(row['phone']),
      contact: this.str(row['contact']),
      address: this.str(row['address']),
      is_active: row['is_active'] as boolean | undefined,
      no_of_locations: row['no_of_locations'] != null ? Number(row['no_of_locations']) : undefined,
      logo: this.pickLogo(row),
      created_at: this.str(row['created_at']),
      updated_at: this.str(row['updated_at']),
      timezone: this.str(row['timezone']) ?? this.str(row['time_zone']),
      admin_user_id: adminUserId != null ? String(adminUserId) : undefined,
      admin_name:
        this.str(row['admin_name']) ?? this.str(owner?.['name']) ?? this.str(admin?.['name']),
      admin_email:
        this.str(row['admin_email']) ?? this.str(owner?.['email']) ?? this.str(admin?.['email']),
    };
  }

  private pickLogo(row: Record<string, any>): string | undefined {
    const candidates = [
      row['logo'],
      row['logo_url'],
      row['logoUrl'],
      row['company_logo'],
      row['image'],
      row['image_url'],
    ];
    for (const value of candidates) {
      if (value != null && String(value).trim()) {
        return String(value).trim();
      }
    }
    return undefined;
  }

  private asRecord(value: any): Record<string, any> | null {
    return value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, any>)
      : null;
  }

  private str(value: any): string | undefined {
    return value != null ? String(value) : undefined;
  }
}
